import {createApi} from '@reduxjs/toolkit/query/react';
import {axiosBaseQuery, REFETCH_POLICY} from 'src/axiosBaseQuery';
import {
    CloneRoleRequest,
    CreateRoleRequest,
    MePermissionsDTO,
    PermissionDTO,
    PermissionDecisionDTO,
    ReplaceRolePermissionsRequest,
    RoleDTO,
    UpdateRoleRequest,
    UserAccessSummaryDTO,
    UserBindingDTO,
    UpsertBindingRequest,
    ReplaceOverridesRequest,
    UpsertPermissionRequest,
} from 'src/types';
import {getBaseURL} from 'src/utils/helper';

/**
 * What a role write answers: the id of the role it actually changed, inside the usual envelope.
 *
 * Not always the one addressed. Editing a shared starting role changes this business's own copy of
 * it, so the screen has to follow the id it gets back rather than the one in its address bar.
 */
export type RoleWriteResult = {payload?: number};

export const iamApi = createApi({
    reducerPath: 'iamApi',
    ...REFETCH_POLICY,
    baseQuery: axiosBaseQuery({
        baseUrl: String(getBaseURL()),
    }),
    tagTypes: ['Roles', 'Role', 'Me', 'Catalog', 'Effective'],
    endpoints: (build) => ({
        /**
         * The caller's own permissions. The argument is a cache key, not a parameter — the server
         * reads the user from the token. Without it every identity shares one cache entry, and the
         * next person to sign in is drawn with the last one's rights.
         */
        getMyPermissions: build.query<MePermissionsDTO, number>({
            query: () => ({url: '/me/permissions', method: 'get'}),
            providesTags: ['Me'],
        }),
        getPermissionCatalog: build.query<PermissionDTO[], void>({
            query: () => ({url: '/admin/permissions', method: 'get'}),
            providesTags: ['Catalog'],
        }),
        createPermission: build.mutation<number, UpsertPermissionRequest>({
            query: (data) => ({url: '/admin/permissions', method: 'post', data}),
            invalidatesTags: ['Catalog'],
        }),
        updatePermission: build.mutation<unknown, {name: string; data: UpsertPermissionRequest}>({
            query: ({name, data}) => ({url: `/admin/permissions/${name}`, method: 'put', data}),
            invalidatesTags: ['Catalog'],
        }),
        deletePermission: build.mutation<unknown, string>({
            query: (name) => ({url: `/admin/permissions/${name}`, method: 'delete'}),
            invalidatesTags: ['Catalog', 'Roles', 'Me'],
        }),
        /**
         * The roles that may be bound: the system ones plus the business's own.
         *
         * Keyed on the business, because a platform-wide caller belongs to none and the server
         * answers with every tenant's roles unless told which one to look at - so switching the
         * active business changed nothing on the access screen. Everyone else is scoped to their
         * own business by the server whatever they pass.
         */
        getRoles: build.query<RoleDTO[], number>({
            query: (businessId) => ({url: '/admin/roles', method: 'get', params: {businessId}}),
            providesTags: ['Roles'],
        }),
        getRole: build.query<RoleDTO, number>({
            query: (roleId) => ({url: `/admin/roles/${roleId}`, method: 'get'}),
            providesTags: (_result, _error, roleId) => [{type: 'Role', id: roleId}],
        }),
        /**
         * The access summary for the people on one page, asked for by id.
         *
         * Keyed on the ids so a page change refetches and a revisit does not. Tagged with the same
         * Effective tag the per-user views use, so granting a role refreshes the list behind it.
         */
        getAccessSummary: build.query<UserAccessSummaryDTO[], number[]>({
            query: (userIds) => ({url: '/admin/users/access-summary', method: 'post', data: userIds}),
            providesTags: (_result, _error, userIds) =>
                userIds.map((id) => ({type: 'Effective' as const, id})),
        }),
        getUserBinding: build.query<UserBindingDTO, number>({
            query: (userId) => ({url: `/admin/users/${userId}/binding`, method: 'get'}),
            providesTags: (_result, _error, userId) => [{type: 'Effective', id: userId}],
        }),
        getEffectivePermissions: build.query<PermissionDecisionDTO[], number>({
            query: (userId) => ({url: `/admin/users/${userId}/effective-permissions`, method: 'get'}),
            // Without a tag nothing could invalidate this, so every mutation had to be followed by a
            // hand-written refetch — one of which will eventually be forgotten, and the screen will
            // show a decision the server no longer makes.
            providesTags: (_result, _error, userId) => [{type: 'Effective', id: userId}],
        }),
        createRole: build.mutation<RoleWriteResult, CreateRoleRequest>({
            query: (data) => ({url: '/admin/roles', method: 'post', data}),
            invalidatesTags: ['Roles'],
        }),
        updateRole: build.mutation<RoleWriteResult, {roleId: number; data: UpdateRoleRequest}>({
            query: ({roleId, data}) => ({url: `/admin/roles/${roleId}`, method: 'put', data}),
            invalidatesTags: (result, _e, {roleId}) => [
                'Roles',
                {type: 'Role', id: roleId},
                {type: 'Role', id: result?.payload ?? roleId},
            ],
        }),
        replaceRolePermissions: build.mutation<
            RoleWriteResult,
            {roleId: number; data: ReplaceRolePermissionsRequest}
        >({
            // The whole set, never a delta. Two people editing the same role concurrently would lose
            // one of the changes with add/remove; replacing is idempotent and has no such race.
            query: ({roleId, data}) => ({url: `/admin/roles/${roleId}/permissions`, method: 'put', data}),
            invalidatesTags: (result, _e, {roleId}) => [
                'Roles',
                {type: 'Role', id: roleId},
                {type: 'Role', id: result?.payload ?? roleId},
                'Me',
            ],
        }),
        cloneRole: build.mutation<RoleWriteResult, {roleId: number; data: CloneRoleRequest}>({
            query: ({roleId, data}) => ({url: `/admin/roles/${roleId}/clone`, method: 'post', data}),
            invalidatesTags: ['Roles'],
        }),
        deleteRole: build.mutation<unknown, number>({
            query: (roleId) => ({url: `/admin/roles/${roleId}`, method: 'delete'}),
            invalidatesTags: ['Roles'],
        }),
        bindUser: build.mutation<unknown, {userId: number; data: UpsertBindingRequest}>({
            query: ({userId, data}) => ({url: `/admin/users/${userId}/binding`, method: 'put', data}),
            invalidatesTags: (_r, _e, {userId}) => ['Me', {type: 'Effective', id: userId}],
        }),
        replaceOverrides: build.mutation<unknown, {userId: number; data: ReplaceOverridesRequest}>({
            // One request for the whole set. It used to be one per permission, so granting a subject of
            // twenty meant twenty writes, twenty cache invalidations and twenty ways to end up half
            // applied.
            query: ({userId, data}) => ({url: `/admin/users/${userId}/overrides`, method: 'put', data}),
            invalidatesTags: (_r, _e, {userId}) => ['Me', {type: 'Effective', id: userId}],
        }),
    }),
});

export const {
    useGetMyPermissionsQuery,
    useGetPermissionCatalogQuery,
    useCreatePermissionMutation,
    useUpdatePermissionMutation,
    useDeletePermissionMutation,
    useGetRolesQuery,
    useGetRoleQuery,
    useGetAccessSummaryQuery,
    useGetUserBindingQuery,
    useGetEffectivePermissionsQuery,
    useCreateRoleMutation,
    useUpdateRoleMutation,
    useReplaceRolePermissionsMutation,
    useCloneRoleMutation,
    useDeleteRoleMutation,
    useBindUserMutation,
    useReplaceOverridesMutation,
} = iamApi;
